export interface PricingPlan {
  id: 'community' | 'starter' | 'startup' | 'business' | 'enterprise';
  name: string;
  badge?: string;
  price: string;
  priceNote?: string;
  tagline: string;
  limits?: string[];
  capabilities: string[];
  cta: {
    label: string;
    href: string;
  };
  highlight?: boolean;
  showOnLanding?: boolean;
  landingOrder?: number;
}

export interface PricingOverage {
  label: string;
  value: string;
}

export interface PricingVolumeDiscount {
  tier: string;
  discount: string;
}

export interface PricingSupportPlan {
  name: string;
  price: string;
  details: string[];
}

export interface PricingProfessionalService {
  name: string;
  price: string;
  description: string;
}

export const pricingPlans: PricingPlan[] = [
  {
    id: 'community',
    name: 'Community',
    badge: 'Self-hosted',
    price: 'Free forever',
    tagline: 'Host Relay yourself with the full control plane and community support.',
    limits: ['Unlimited functions & invocations', 'Bring-your-own infrastructure', 'Community Discord support'],
    capabilities: ['Full registry & execution platform', 'Open source governance templates', 'Access to public registry & CLI'],
    cta: { label: 'Download CLI', href: '/start' },
    highlight: false,
    showOnLanding: true,
    landingOrder: 0,
  },
  {
    id: 'starter',
    name: 'Starter',
    badge: '14-day trial',
    price: '$25/mo',
    tagline: 'Managed cloud experience for builders who want policy and telemetry out of the box.',
    limits: ['3 namespaces', '100 functions', '500k invocations/month', '5 GB storage', '3 team members'],
    capabilities: [
      'Automatic HTTPS & global CDN',
      'Basic analytics dashboards',
      'CLI + Studio access',
      'Function composition & lifecycle hooks',
      'Retry logic, cron scheduling, webhook triggers',
    ],
    cta: { label: 'Start 14-day trial', href: '/start' },
    highlight: true,
    showOnLanding: true,
    landingOrder: 1,
  },
  {
    id: 'startup',
    name: 'Startup',
    price: '$49/mo',
    tagline: 'Scale teams with richer analytics, integrations, and private functions.',
    limits: ['10 namespaces', '500 functions', '1M invocations/month', '10 GB storage', '5 team members'],
    capabilities: [
      'Custom domains & private functions',
      'Advanced analytics & custom rate limits',
      'Priority webhook processing',
      'Workflow builder (2-step) & dependency management',
      'WASM runtime + AI provider integrations',
      'Email support (24h response)',
    ],
    cta: { label: 'Upgrade to Startup', href: '/contact' },
    highlight: false,
    showOnLanding: false,
  },
  {
    id: 'business',
    name: 'Business',
    price: '$299/mo',
    tagline: 'Production-grade control plane with compliance, multi-region, and priority support.',
    limits: ['50 namespaces', '5,000 functions', '10M invocations/month', '100 GB storage', '20 team members'],
    capabilities: [
      'Priority support (chat + email)',
      'Advanced security (WAF) & audit logs (90 days)',
      'Multi-region deployment & staging environments',
      'SSO (SAML 2.0) & namespace automation',
      'Automated backups & data residency options',
    ],
    cta: { label: 'Talk to sales', href: '/contact' },
    highlight: false,
    showOnLanding: true,
    landingOrder: 2,
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    badge: 'From $1,499/mo',
    price: 'Custom pricing',
    tagline: 'Dedicated infrastructure, regulated compliance, and 24/7 partnership.',
    limits: ['Unlimited namespaces & functions', 'Custom invocation & storage limits', 'Unlimited team members'],
    capabilities: [
      'Single-tenant or on-prem deployment',
      'Custom SLA up to 99.99%',
      '24/7 phone + dedicated Slack support',
      'Dedicated account manager & success partner',
      'HIPAA, SOC 2 Type II, GDPR support',
      'Professional services & white-label options',
    ],
    cta: { label: 'Schedule enterprise briefing', href: '/contact' },
    highlight: false,
    showOnLanding: true,
    landingOrder: 3,
  },
];

export const pricingAddOns = [
  {
    name: 'Edge caching & warm pools',
    description: 'Pre-warm functions across global regions with cache policies tailored per endpoint.',
  },
  {
    name: 'Compliance accelerator',
    description: 'Automated evidence collection, policy attestations, and SOC 2 tooling.',
  },
  {
    name: 'Dedicated telemetry warehouse',
    description: 'Near real-time feed of execution metrics into your analytics stack.',
  },
];

export const pricingOverages: PricingOverage[] = [
  { label: 'Additional invocations', value: '$0.50 per million' },
  { label: 'Additional storage', value: '$0.10 per GB / month' },
  { label: 'Additional functions', value: '$0.02 per function / month' },
  { label: 'Additional namespaces', value: '$5 per namespace / month' },
  { label: 'Additional team members', value: '$10 per member / month' },
];

export const pricingVolumeDiscounts: PricingVolumeDiscount[] = [
  { tier: '100M+ invocations/month', discount: '20% discount' },
  { tier: '1B+ invocations/month', discount: '35% discount' },
  { tier: '10B+ invocations/month', discount: '50% discount' },
];

export const pricingSupportPlans: PricingSupportPlan[] = [
  {
    name: 'Standard Support',
    price: '$199/mo',
    details: ['4-hour response SLA', 'Email + chat', 'Business hours coverage'],
  },
  {
    name: 'Premium Support',
    price: '$999/mo',
    details: ['1-hour response SLA', '24/7 coverage', 'Dedicated Slack channel', 'Quarterly reviews'],
  },
  {
    name: 'White Glove Support',
    price: '$4,999/mo',
    details: ['15-minute response SLA', 'Dedicated engineer', 'Weekly sync calls', 'Custom training'],
  },
];

export const pricingProfessionalServices: PricingProfessionalService[] = [
  {
    name: 'Migration assistance',
    price: '$5,000 flat',
    description: 'Data migration, function porting, and team training to bring Relay online quickly.',
  },
  {
    name: 'Custom integration',
    price: 'Starting at $10,000',
    description: 'Bespoke adapters, legacy system integrations, or API gateway implementations.',
  },
  {
    name: 'Architecture review',
    price: '$2,500',
    description: 'Performance audit, security review, and optimization recommendations delivered in a detailed report.',
  },
];
